import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calendar } from 'lucide-react';
import { getGoalAnalytics } from '../../../services/studentGoalsService';

const AnalyticsVisualization = () => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [timeRange, setTimeRange] = useState('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - parseInt(timeRange));
      
      const data = await getGoalAnalytics(null, startDate?.toISOString());
      
      // Transform data for charts
      const chartData = processDataForCharts(data);
      setAnalyticsData(chartData);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const processDataForCharts = (data) => {
    // Group by date and calculate daily averages
    const dailyData = {};
    
    data?.forEach(record => {
      const date = new Date(record.created_at)?.toLocaleDateString();
      if (!dailyData?.[date]) {
        dailyData[date] = {
          date,
          totalScore: 0,
          count: 0,
          sessions: 0
        };
      }
      dailyData[date].totalScore += record?.accuracy_score;
      dailyData[date].count += 1;
      dailyData[date].sessions += 1;
    });

    // Calculate averages and sort by date
    return Object.values(dailyData)?.map(day => ({
        date: day?.date,
        accuracy: (day?.totalScore / day?.count)?.toFixed(2),
        sessions: day?.sessions
      }))?.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-purple-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-red-500 text-center">
          <p className="font-semibold mb-2">Error loading analytics</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-600" />
            Performance Analytics
          </h2>
          <div className="flex gap-2">
            {['7', '30', '90']?.map((days) => (
              <button
                key={days}
                onClick={() => setTimeRange(days)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  timeRange === days
                    ? 'bg-purple-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {days} Days
              </button>
            ))}
          </div>
        </div>

        {analyticsData?.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No practice data for this period.</p>
            <p className="text-sm text-gray-500 mt-2">
              Start practicing to see your progress!
            </p>
          </div>
        ) : (
          <>
            {/* Accuracy Trend Chart */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Pronunciation Accuracy Trend
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="accuracy" 
                    stroke="#9333ea" 
                    strokeWidth={3}
                    name="Accuracy (%)"
                    dot={{ fill: '#9333ea', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Practice Frequency Chart */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Practice Session Frequency
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar 
                    dataKey="sessions" 
                    fill="#3b82f6" 
                    name="Sessions"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
      {/* Key Insights */}
      {analyticsData?.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-purple-600 mb-1">Average Accuracy</div>
              <div className="text-2xl font-bold text-purple-900">
                {(analyticsData?.reduce((sum, d) => sum + parseFloat(d?.accuracy), 0) / analyticsData?.length)?.toFixed(1)}%
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 mb-1">Total Sessions</div>
              <div className="text-2xl font-bold text-blue-900">
                {analyticsData?.reduce((sum, d) => sum + d?.sessions, 0)}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 mb-1">Practice Days</div>
              <div className="text-2xl font-bold text-green-900">
                {analyticsData?.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsVisualization;